import { moveItemInArray } from '@angular/cdk/drag-drop';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { ConnectionAdapter } from '../../adapters/connection.adapter';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { AppState } from '../../app.reducer';
import {
  createJob,
  createJobSuccess,
  deleteConnectionForJob,
  deleteConnectionForJobFailure,
  deleteConnectionForJobSuccess,
  deleteJobFailure,
  loadJobs,
  loadJobsFailure,
  loadJobsSuccess,
  sortJobs,
  sortJobsFailure,
  sortJobsSuccess,
} from './jobs.actions';
import { selectJobsOpenAndClosed } from './jobs.reducer';

@Injectable()
export class JobsEffects {
  private readonly actions$ = inject(Actions);
  private readonly jobsAdapter = inject(JobsAdapter);
  private readonly store = inject(Store<AppState>);
  private readonly connectionAdapter = inject(ConnectionAdapter);
  
  loadJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadJobs),
      exhaustMap(() =>
        this.jobsAdapter.getCurrentJobs().pipe(
          map((result) => loadJobsSuccess({ jobs: result.jobs, initiativeID: result.initiativeID })),
          catchError((error) => {
            return of(loadJobsFailure({ error }));
          })
        )
      )
    )
  );

  createJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createJob),
      mergeMap((props) =>
        this.jobsAdapter.createJob(props.job).pipe(
          map((response) => createJobSuccess({ job: { ...props.job, id: response.id } })),
          catchError((error) => {
            return of(deleteJobFailure({ error }));
          })
        )
      )
    )
  );

  sortJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sortJobs),
      concatLatestFrom(() => this.store.select(selectJobsOpenAndClosed)),
      mergeMap(([props, jobs]) => {
        moveItemInArray(jobs, props.oldIndex, props.newIndex);

        return this.jobsAdapter.sortJobs(jobs.map((job) => job.id)).pipe(
          map(() => sortJobsSuccess({ jobs: jobs.map((job, index) => ({ ...job, index })) })),
          catchError((error) => {
            return of(sortJobsFailure({ error }));
          })
        );
      })
    )
  );

  /*
  updateJob$ = createEffect(() => this.actions$.pipe(
    ofType(updateJob),
    mergeMap((props) => this.jobsAdapter.editJob(props.job).pipe(
      map(() => updateJobSuccess({ job: props.job })),
      catchError(error => {
        return of(updateJobFailure({ error }))
      }),
    ))
  ));

  deleteJob$ = createEffect(() => this.actions$.pipe(
    ofType(deleteJob),
    mergeMap((props) => this.jobsAdapter.deleteJob(props.job).pipe(
      map(() => deleteJobSuccess({ id: props.job.id })),
      catchError(error => {
        return of(deleteJobFailure({ error }))
      }),
    ))
  ));
  //*/

  loadJobsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadJobsFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  deleteConnectionForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteConnectionForJob),
      mergeMap((props) =>
        this.connectionAdapter.deleteConnectionForJob(props.job.id).pipe(
          map(() => deleteConnectionForJobSuccess(props)),
          catchError((error) => {
            return of(deleteConnectionForJobFailure({ error }));
          })
        )
      )
    )
  );
}
